import CloseIcon from '@mui/icons-material/Close';
import DifferenceIcon from '@mui/icons-material/Difference';
import InfoIcon from '@mui/icons-material/Info';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import Warning from '@mui/icons-material/Warning';
import { createTheme, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { lightBlue, red } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import root from 'react-shadow';

import { SITUACAO_CARREGAMENTO_VERSAO_MINUTA } from '@/store/SalaSessaoStore';
import Configuracao from '@/util/Configuracao';
import ErrorUtil from '@/util/ErrorUtil';
import HtmlDiff from '@/util/HtmlDiff';

import ReactComponent from '../ReactComponent';

// eslint-disable-next-line unicorn/no-top-level-side-effects
dayjs.extend(customParseFormat);

const temaVersoesMinutaVoto = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            xl: 1537, // 125% (1536px)
            // @ts-ignore
            xxl: 2559 // 75% (2560px)
        }
    }
});

const DialogVersoesMinutaVoto = styled(Dialog)(() => ({
    [temaVersoesMinutaVoto.breakpoints.between('xs', 'xl')]: {
        '& .MuiDialog-container': {
            '& .MuiPaper-root': {
                minWidth: '95%'
            }
        }
    },
    // @ts-ignore
    [temaVersoesMinutaVoto.breakpoints.between('xl', 'xxl')]: {
        '& .MuiDialog-container': {
            '& .MuiPaper-root': {
                minWidth: '75%'
            }
        }
    },
    // @ts-ignore
    [temaVersoesMinutaVoto.breakpoints.up('xxl')]: {
        '& .MuiDialog-container': {
            '& .MuiPaper-root': {
                minWidth: '50%'
            }
        }
    }
}));

class VisualizadorVersoesMinutaVoto extends ReactComponent {
    static FORMATO_DATAHORA_SAIDA = 'DD/MM/YYYY HH:mm:ss';
    static larguraAbas = 390; //px
    static corInsercoesTexto = 'PaleGreen';
    static corExclusoesTexto = 'LightPink';
    static alturaMinimaDialogContentCssFormat = `calc(100vh - 265px)`;

    titulo = '';
    aberto = false;
    abaAtiva = 0;
    versoesCarregadas = false;
    exibirDiff = true; // [TODO] Permitir que o usuário selecione se ele quer visualizar a versão com o diff ou a versão original

    constructor(props) {
        super(props);

        makeObservable(this, {
            titulo: observable,
            aberto: observable,
            abaAtiva: observable,
            versoesCarregadas: observable,
            exibirDiff: observable,
            registro: computed,
            totalVersoesParaListar: computed,
            versoesMinutaVotoParaListar: computed,
            isVersoesCarregadas: computed,
            carregarVersoes: action,
            setVersoesCarregadas: action,
            abrir: action,
            fechar: action,
            handleClickVisualizadorMinutaVoto: action,
            handleCloseVisualizadorMinutaVoto: action,
            carregarConteudoVersaoMinutaVoto: action,
            atualizarPainelConteudoMinutaVoto: action,
            renderEditorMinuta: computed
        });

        this.createReaction(
            reaction(
                () => this.registro,
                async () => {
                    if (!this.registro.editorMinutaVoto) {
                        await this.salaSessaoStore.carregarEditorVoto(this.registro);
                    }

                    if (!this.registro.conteudoVoto) {
                        await this.salaSessaoStore.carregarConteudoVoto(this.registro);
                    }
                }
            )
        );

        this.createReaction(
            reaction(
                () => this.aberto,
                async () => {
                    if (!this.aberto) {
                    	return;
                    }

                    try {
                        await this.carregarVersoes();
                    } catch (error) {
                        this.mensagemStore.abrirMensagemErro(ErrorUtil.getMensagemErro(error));
                        this.fechar();
                    }
                }
            )
        );
    }

    async componentDidMount() {
        try {
            if (this.registro) {
                if (!this.registro.editorMinutaVoto) {
                    this.salaSessaoStore.carregarEditorVoto(this.registro);
                }

                if (!this.registro.conteudoVoto) {
                    this.salaSessaoStore.carregarConteudoVoto(this.registro);
                }
            }
        } catch (error) {
            console.error(
                `Falha na inicialização do componente VisualizadorVersoesMinutaVoto: ${ErrorUtil.getMensagemErro(error)}`
            );
            throw new Error('Falha na inicialização do componente VisualizadorVersoesMinutaVoto', { cause: error });
        }
    }

    get registro() {
        return this.observableProps.registro;
    }

    get totalVersoesParaListar() {
        return this.versoesMinutaVotoParaListar ? this.versoesMinutaVotoParaListar.length : 0;
    }

    get versoesMinutaVotoParaListar() {
        return this.registro.versoesMinutaVoto;
    }

    get isVersoesCarregadas() {
        const isVersaoCarregadaOuComErro = (v) => {
            return !!(
                v.conteudo !== undefined ||
                (v.statusCarregamento && v.statusCarregamento !== SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO)
            );
        };

        return this.versoesMinutaVotoParaListar.every(isVersaoCarregadaOuComErro);
    }

    async carregarVersoes() {
        this.setVersoesCarregadas(false);
        this.registro.versoesMinutaVoto = null;
        await this.salaSessaoStore.consultarVersoesMinutaVoto(this.registro);
        this.setVersoesCarregadas(true);
    }

    setVersoesCarregadas(valor) {
        this.versoesCarregadas = valor;
    }

    abrir = () => {
        this.aberto = true;
    };

    fechar = () => {
        this.aberto = false;
        this.abaAtiva = 0;
        this.registro.versoesMinutaVoto = null;
        this.setVersoesCarregadas(false);
    };

    handleClickVisualizadorMinutaVoto = () => {
        console.log(
            `Abrindo tela de visualização de versões da minuta do voto... [${this.registro.processo.numeroCompleto}]`
        );
        this.abrir();
    };

    handleCloseVisualizadorMinutaVoto = () => {
        console.log(
            `Fechando tela de visualização de versões da minuta do voto... [${this.registro.processo.numeroCompleto}]`
        );
        this.fechar();
    };

    get renderConteudoVersaoMinutaCarregando() {
        return (
            <Box style={this.styleEspacoConteudoVersaoMinuta}>
                <CircularProgress
                    disableShrink={true}
                    size={96}
                />
            </Box>
        );
    }

    renderTabPanel = (index) => {
        if (!this.aberto || !this.versoesCarregadas) {
            return null;
        }

        return (
            <Box
                role="tabpanel"
                key={index}
                hidden={this.abaAtiva !== index}
                id={`tabpanel-versao-minuta-${index}`}
                aria-labelledby={`tab-versao-minuta-${index}`}
                sx={{
                    display: 'flex',
                    maxHeight: `100%`
                }}
            >
                {this.abaAtiva === index &&
                    (this.exibirDiff
                        ? this.renderDiffVersoesMinuta(index)
                        : this.renderConteudoVersaoMinuta(this.versoesMinutaVotoParaListar[index]))}
            </Box>
        );
    };

    renderDiffVersoesMinuta(index) {
        if (
            !this.versoesMinutaVotoParaListar[index] ||
            this.versoesMinutaVotoParaListar[index].statusCarregamento ===
                SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO ||
            this.versoesMinutaVotoParaListar[index].conteudo === undefined ||
            this.versoesMinutaVotoParaListar[index].conteudo === null ||
            (index + 1 < this.versoesMinutaVotoParaListar.length &&
                (!this.versoesMinutaVotoParaListar[index + 1] ||
                    this.versoesMinutaVotoParaListar[index + 1].statusCarregamento ===
                        SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO ||
                    this.versoesMinutaVotoParaListar[index + 1].conteudo === undefined ||
                    this.versoesMinutaVotoParaListar[index + 1].conteudo === null))
        ) {
            return this.renderConteudoVersaoMinutaCarregando;
        }

        if (this.versoesMinutaVotoParaListar[index].statusCarregamento === SITUACAO_CARREGAMENTO_VERSAO_MINUTA.FALHOU) {
            const corMensagem = red[900];

            return (
                <Box sx={this.styleEspacoConteudoVersaoMinuta}>
                    <Stack
                        spacing={{ xs: 1, sm: 2, md: 3 }}
                        sx={{ justifyContent: 'center', alignItems: 'center' }}
                    >
                        <SentimentVeryDissatisfiedIcon sx={{ width: '96px', height: '96px', fill: corMensagem }} />
                        <Typography color={corMensagem}>
                            Erro ao carregar a versão {this.totalVersoesParaListar - index} da minuta de voto.
                        </Typography>
                        <Typography color={corMensagem}>
                            {this.versoesMinutaVotoParaListar[index].erroCarregamento}
                        </Typography>
                    </Stack>
                </Box>
            );
        }

        const conteudoAntigo =
            index + 1 < this.totalVersoesParaListar ? this.versoesMinutaVotoParaListar[index + 1].conteudo : '';
        const conteudoNovo = this.versoesMinutaVotoParaListar[index].conteudo;

        console.log(`Comparando versão do índice ${index} com a versão do índice ${index + 1}...`);
        return this.renderComparador(conteudoAntigo, conteudoNovo, this.versoesMinutaVotoParaListar[index]);
    }

    formatHTMLForDiff(htmlStr) {
        return new DOMParser().parseFromString(htmlStr, 'text/html').body.innerText;
    }

    /** @type {import('react').CSSProperties} */
    get styleConteudoVersaoMinuta() {
        return {
            display: 'initial',
            flexGrow: 1,
            overflow: 'auto',
            boxSizing: 'border-box',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderRadius: '4px',
            paddingTop: '16px',
            paddingLeft: '16px',
            paddingRight: '16px',
            height: VisualizadorVersoesMinutaVoto.alturaMinimaDialogContentCssFormat
        };
    }

    /** @type {import('react').CSSProperties} */
    get styleEspacoConteudoVersaoMinuta() {
        return {
            display: 'flex',
            flexGrow: 1,
            overflow: 'auto',
            boxSizing: 'border-box',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderRadius: '4px',
            paddingTop: '16px',
            paddingLeft: '16px',
            paddingRight: '16px',
            justifyContent: 'center',
            alignItems: 'center',
            height: VisualizadorVersoesMinutaVoto.alturaMinimaDialogContentCssFormat
        };
    }

    renderComparador(conteudoAntigo, conteudoNovo, versaoMinuta) {
        if (conteudoNovo === null || conteudoNovo === undefined) {
            throw new ReferenceError(
                `Parâmetro inválido para \`renderComparador\`: conteúdo da versão da minuta de voto mais recente não pode ser nulo.`
            );
        }

        if (conteudoAntigo === null || conteudoAntigo === undefined) {
            throw new ReferenceError(
                `Parâmetro inválido para \`renderComparador\`: conteúdo da versão da minuta de voto mais antiga não pode ser nulo.`
            );
        }

        if (!versaoMinuta.diff) {
            const htmlDiff = HtmlDiff.execute(conteudoAntigo, conteudoNovo);
            runInAction(() => (versaoMinuta.diff = htmlDiff));
        }

        if (!versaoMinuta.mudancas) {
            this.salaSessaoStore.calcularEstatisticasMudancasTexto(versaoMinuta, versaoMinuta.diff);
        }

        const diffStyles = `
            del.diffdel {
                background-color: ${VisualizadorVersoesMinutaVoto.corExclusoesTexto};
            }

            del.diffmod {
                background-color: ${VisualizadorVersoesMinutaVoto.corExclusoesTexto};
            }

            ins.diffmod {
                background-color: ${VisualizadorVersoesMinutaVoto.corInsercoesTexto};
                text-decoration: initial;
            }

            ins.diffins {
                background-color: ${VisualizadorVersoesMinutaVoto.corInsercoesTexto};
                text-decoration: initial;
            }

            figure del.diffmod,
            figure ins.diffmod,
            figure ins.diffins,
            figure del.diffdel {
                display: block;
            }

            figure {
                text-align: center;
            }
        `;

        return (
            <Box style={this.styleConteudoVersaoMinuta}>
                <root.div>
                    <style>{diffStyles}</style>
                    <div
                        data-sentry-mask
                        dangerouslySetInnerHTML={{ __html: versaoMinuta.diff }}
                    ></div>
                </root.div>
            </Box>
        );
    }

    renderConteudoVersaoMinuta(versaoMinuta) {
        if (versaoMinuta.statusCarregamento === SITUACAO_CARREGAMENTO_VERSAO_MINUTA.EM_ANDAMENTO) {
            return this.renderConteudoVersaoMinutaCarregando;
        }

        return (
            <Box style={this.styleConteudoVersaoMinuta}>
                <root.div>
                    <div
                        data-sentry-mask
                        dangerouslySetInnerHTML={{ __html: versaoMinuta.conteudo }}
                    ></div>
                </root.div>
            </Box>
        );
    }

    async carregarConteudoVersaoMinutaVoto(index) {
        if (this.versoesMinutaVotoParaListar[index]?.conteudo) {
            console.log(
                `Conteúdo da versão da minuta já está carregado em memória RAM... [${this.#versaoMinutaToString(this.versoesMinutaVotoParaListar[index])}]`
            );

            if (this.exibirDiff && index + 1 < this.versoesMinutaVotoParaListar.length) {
                if (this.versoesMinutaVotoParaListar[index + 1]?.conteudo) {
                    console.log(
                        `Conteúdo da versão da minuta (mais antiga, para comparação) já está carregado em memória RAM... [${this.#versaoMinutaToString(this.versoesMinutaVotoParaListar[index + 1])}]`
                    );
                    return;
                } else {
                    console.log(
                        `Conteúdo ainda não disponível em memória RAM para viabilizar o diff... [${this.#versaoMinutaToString(this.versoesMinutaVotoParaListar[index + 1])}]`
                    );
                }
            } else {
                return;
            }
        } else {
            console.log(
                `Conteúdo da versão da minuta ainda não disponível em memória RAM... [${this.#versaoMinutaToString(this.versoesMinutaVotoParaListar[index])}]`
            );
        }

        const TENTAR_CARREGAR_NOVAMENTE_SE_CONTEUDO_VAZIO = true;

        try {
            if (
                this.versoesMinutaVotoParaListar[index].conteudo === undefined ||
                (this.versoesMinutaVotoParaListar[index].conteudo === '' && TENTAR_CARREGAR_NOVAMENTE_SE_CONTEUDO_VAZIO)
            ) {
                console.log(`Carregando versão sob demanda...`);
                const versaoMinutaCarregada = await this.salaSessaoStore.consultarVersaoMinutaVoto(
                    this.registro,
                    this.versoesMinutaVotoParaListar[index],
                    true
                );
                runInAction(() => (this.versoesMinutaVotoParaListar[index] = versaoMinutaCarregada));
            }

            if (
                this.exibirDiff &&
                this.versoesMinutaVotoParaListar[index].conteudo &&
                (!this.versoesMinutaVotoParaListar[index + 1] ||
                    this.versoesMinutaVotoParaListar[index + 1].conteudo === undefined ||
                    (this.versoesMinutaVotoParaListar[index + 1].conteudo === '' &&
                        TENTAR_CARREGAR_NOVAMENTE_SE_CONTEUDO_VAZIO))
            ) {
                console.log(`Carregando versão anterior para cálculo das diferenças...`);
                const versaoMinutaCarregada = await this.salaSessaoStore.consultarVersaoMinutaVoto(
                    this.registro,
                    this.versoesMinutaVotoParaListar[index + 1],
                    true
                );
                runInAction(() => (this.versoesMinutaVotoParaListar[index + 1] = versaoMinutaCarregada));
            }
        } catch (error) {
            this.mensagemStore.abrirMensagemErro(ErrorUtil.getMensagemErro(error));
        }
    }

    atualizarPainelConteudoMinutaVoto = (event, newValue) => {
        //console.log(event);
        this.abaAtiva = newValue;
    };

    getTabLabel = (versaoMinuta, index) => {
        if (!this.aberto || !this.versoesCarregadas) {
            return null;
        }

        const exibirEstatisticasTextoParaVersaoInicial = false;

        return (
            <Stack direction="row">
                <Avatar
                    variant="rounded"
                    sx={{ width: 56, height: 24 }}
                >
                    v{this.totalVersoesParaListar - index}
                </Avatar>
                <Typography sx={{ ml: 1 }}>{this.getLabelAba(versaoMinuta)}</Typography>
                {this.getInfoMudancas(versaoMinuta, index, exibirEstatisticasTextoParaVersaoInicial)}
            </Stack>
        );
    };

    getLabelAba(versaoMinuta) {
        const dataHora = versaoMinuta.ultimaAlteracao;
        const dataHoraFormatada = dayjs(dataHora, Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO).format(
            VisualizadorVersoesMinutaVoto.FORMATO_DATAHORA_SAIDA
        );
        return dataHoraFormatada;
    }

    getInfoMudancas(versaoMinuta, index, exibirEstatisticasTextoParaVersaoInicial) {
        if (!(index + 1 < this.totalVersoesParaListar || exibirEstatisticasTextoParaVersaoInicial)) {
            return null;
        }

        const textoQtdInsercoes =
            versaoMinuta?.mudancas?.totalInsercoes !== undefined ? `+${versaoMinuta.mudancas.totalInsercoes}` : '+ ?';
        const textoQtdExclusoes =
            versaoMinuta?.mudancas?.totalExclusoes !== undefined ? `-${versaoMinuta.mudancas.totalExclusoes}` : '- ?';

        const textoInsercoes = versaoMinuta?.mudancas?.totalInsercoes
            ? `${versaoMinuta.mudancas.totalInsercoes} alteraç${versaoMinuta.mudancas.totalInsercoes > 1 ? 'ões' : 'ão'} de acréscimo de texto`
            : 'Nenhuma inclusão de texto';
        const textoExclusoes = versaoMinuta?.mudancas?.totalExclusoes
            ? `${versaoMinuta.mudancas.totalExclusoes} alteraç${versaoMinuta.mudancas.totalExclusoes > 1 ? 'ões' : 'ão'} de remoção de texto`
            : 'Nenhuma remoção de texto';

        return (
            <>
                <Tooltip title={<Typography>{textoInsercoes}</Typography>}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            ml: 1,
                            width: 56,
                            height: 24,
                            backgroundColor: VisualizadorVersoesMinutaVoto.corInsercoesTexto
                        }}
                    >
                        {textoQtdInsercoes}
                    </Avatar>
                </Tooltip>
                <Tooltip title={<Typography>{textoExclusoes}</Typography>}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            ml: 1,
                            width: 56,
                            height: 24,
                            backgroundColor: VisualizadorVersoesMinutaVoto.corExclusoesTexto
                        }}
                    >
                        {textoQtdExclusoes}
                    </Avatar>
                </Tooltip>
                {textoQtdInsercoes === '+0' && textoQtdExclusoes === '-0' && (
                    <Tooltip
                        title={
                            <Typography>
                                {versaoMinuta.semAlteracaoConteudo
                                    ? 'Sem alterações no conteúdo (possível falha no editor da minuta)'
                                    : 'Sem alterações no texto, porém, com alterações na formatação de texto (cores, margens, negritos, itálicos, sublinhados, etc.)'}
                            </Typography>
                        }
                    >
                        {versaoMinuta.semAlteracaoConteudo ? (
                            <Warning sx={{ ml: 1, fill: 'darkorange' }} />
                        ) : (
                            <InfoIcon sx={{ ml: 1 }} />
                        )}
                    </Tooltip>
                )}
            </>
        );
    }

    get renderEditorMinuta() {
        if (!this.registro.editorMinutaVoto) {
            return null;
        }

        return (
            <>
                <Typography
                    display="inline"
                    sx={{ color: lightBlue[900], fontWeight: 'bold', ml: 1 }}
                >
                    (Editor:&nbsp;
                </Typography>
                <Typography
                    display="inline"
                    sx={{ color: lightBlue[900], fontWeight: 'bold' }}
                >
                    {this.registro.editorMinutaVoto}
                </Typography>
                <Typography
                    display="inline"
                    sx={{ color: lightBlue[900], fontWeight: 'bold' }}
                >
                    )
                </Typography>
            </>
        );
    }

    renderTituloDialog() {
        return (
            <Stack direction="row">
                <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-start' }}>
                    <Typography
                        display="inline"
                        sx={{ fontWeight: 'bold' }}
                    >
                        Comparador de versões da minuta de voto
                    </Typography>
                    {this.renderEditorMinuta}
                </Box>
                <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
                    <Typography
                        display="inline"
                        sx={{ fontWeight: 'bold' }}
                    >
                        {this.registro.processo.numeroCompleto}
                    </Typography>
                    <Typography
                        display="inline"
                        sx={{ fontStyle: 'italic', ml: 1 }}
                    >
                        ({this.registro.processo.relator ? this.registro.processo.relator.sigla : 'Relator indefinido'})
                    </Typography>
                </Box>
            </Stack>
        );
    }

    renderBotaoVisualizarVersoesMinutaVoto() {
        if (!this.registro.liberadoPeloRelator || !this.registro.editorMinutaVoto || !this.registro.conteudoVoto) {
            return null;
        }

        return (
            <Tooltip title={<Typography>Visualizar versões da minuta de voto</Typography>}>
                <DifferenceIcon
                    sx={{
                        cursor: 'pointer',
                        fillOpacity: '0.7',
                        fill: 'black',
                        ml: 1
                    }}
                    onClick={this.handleClickVisualizadorMinutaVoto}
                />
            </Tooltip>
        );
    }

    render() {
        return (
            <>
                {this.renderBotaoVisualizarVersoesMinutaVoto()}
                <DialogVersoesMinutaVoto
                    open={this.aberto}
                    onClose={this.handleCloseVisualizadorMinutaVoto}
                >
                    <DialogTitle sx={{ pt: 3, pl: 5, pr: 5 }}>{this.renderTituloDialog()}</DialogTitle>
                    <DialogContent>
                        {!this.versoesCarregadas ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    height: VisualizadorVersoesMinutaVoto.alturaMinimaDialogContentCssFormat,
                                    p: 2,
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <CircularProgress
                                    disableShrink={true}
                                    size={96}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    height: VisualizadorVersoesMinutaVoto.alturaMinimaDialogContentCssFormat,
                                    p: 2,
                                    bgcolor: 'background.paper'
                                }}
                            >
                                {this.versoesMinutaVotoParaListar.length ? (
                                    <>
                                        <Tabs
                                            orientation="vertical"
                                            variant="scrollable"
                                            scrollButtons={true}
                                            allowScrollButtonsMobile
                                            value={this.abaAtiva}
                                            onChange={this.atualizarPainelConteudoMinutaVoto}
                                            indicatorColor="primary"
                                            aria-label="Versões da minuta do voto"
                                            sx={{
                                                pr: 1,
                                                borderColor: 'divider',
                                                minWidth: `${VisualizadorVersoesMinutaVoto.larguraAbas}px`
                                            }}
                                        >
                                            {this.versoesMinutaVotoParaListar.map((versaoMinuta, index) => {
                                                return (
                                                    <Tab
                                                        key={index}
                                                        tabIndex={index}
                                                        sx={{ minWidth: '100%', alignItems: 'flex-start' }}
                                                        label={this.getTabLabel(versaoMinuta, index)}
                                                        id={`tab-versao-minuta-${index}`}
                                                        aria-controls={`tabpanel-versao-minuta-${index}`}
                                                        onClick={async () =>
                                                            await this.carregarConteudoVersaoMinutaVoto(index)
                                                        }
                                                    />
                                                );
                                            })}
                                        </Tabs>
                                        <Box
                                            sx={{
                                                width: `calc(100% - ${VisualizadorVersoesMinutaVoto.larguraAbas}px)`
                                            }}
                                        >
                                            {this.versoesMinutaVotoParaListar.map((versaoMinuta, index) => {
                                                return this.renderTabPanel(index);
                                            })}
                                        </Box>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexGrow: 1,
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Warning
                                            fontSize="large"
                                            sx={{ fill: 'darkorange' }}
                                        />
                                        <Typography sx={{ ml: 2, fontSize: 'large', color: 'darkorange' }}>
                                            Versões inexistentes ou indisponíveis.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ pr: 5, pb: 3 }}>
                        <Button
                            color="primary"
                            variant="contained"
                            endIcon={<CloseIcon />}
                            onClick={this.handleCloseVisualizadorMinutaVoto}
                        >
                            Fechar
                        </Button>
                    </DialogActions>
                </DialogVersoesMinutaVoto>
            </>
        );
    }

    #versaoMinutaToString(versaoMinuta) {
        return `[gedId:${versaoMinuta.docId}|extensaoVersaoGed:${versaoMinuta.extensao}|ultimaAlteracaoGed:${versaoMinuta.ultimaAlteracao}]`;
    }
}

export default inject('stores')(observer(VisualizadorVersoesMinutaVoto));
